(() => {
  const connection = navigator.connection;
  if (connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType)) return;
  const probe = document.createElement('link');
  const navigationPrefetch = typeof HTMLScriptElement !== 'undefined' && HTMLScriptElement.supports?.('speculationrules');
  if (!navigationPrefetch && !probe.relList?.supports?.('prefetch')) return;
  const warmed = new Set();
  const images = new Map();
  let passiveCount = 0;
  let hoverTimer;
  const productPath = /\/products\/[^/]+\/?$/;
  const valid = anchor => {
    if (!anchor?.href || anchor.hasAttribute('download') || 'noInstant' in anchor.dataset) return null;
    const url = new URL(anchor.href, location.href);
    if (url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) return null;
    if (/\/(cart|checkout|account)(\/|$)/.test(url.pathname)) return null;
    if (url.pathname + url.search === location.pathname + location.search) return null;
    if (url.search && !productPath.test(url.pathname) && !('instantAllowQueryString' in document.body.dataset) && !('instant' in anchor.dataset)) return null;
    url.hash = '';
    return url;
  };
  const hint = (url, type) => {
    if (type === 'document' && navigationPrefetch) {
      // Fetch navigation HTML without running its scripts, pixels, or bundle code.
      const rules = document.createElement('script');
      rules.type = 'speculationrules';
      rules.textContent = JSON.stringify({prefetch: [{urls: [url]}]});
      document.head.appendChild(rules);
      return;
    }
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = type;
    link.fetchPriority = 'low';
    link.href = url;
    document.head.appendChild(link);
  };
  const warm = (anchor, passive = false) => {
    const url = valid(anchor);
    if (!url || warmed.has(url.href) || warmed.size >= 4 || (passive && passiveCount >= 2)) return;
    warmed.add(url.href);
    if (passive) passiveCount++;
    hint(url.href, 'document');
    if (!productPath.test(url.pathname)) return;
    const card = anchor.closest('product-block');
    const image = card?.querySelector('.product-block__image--active img') || card?.querySelector('.product-block__image--primary img');
    const variant = url.searchParams.get('variant');
    const defaultImage = (!variant || variant === card?.dataset?.pdpPrefetchVariant) && card?.dataset?.pdpPrefetchImage;
    const raw = defaultImage || image?.getAttribute('src') || image?.dataset?.src || image?.dataset?.manualSrc;
    if (!raw) return;
    const originalWidth = Number(new URL(raw, location.href).searchParams.get('width'));
    if (!(originalWidth > 0)) return;
    const src = new URL(defaultImage || image?.currentSrc || raw, location.href);
    if (!src.pathname.includes('/cdn/shop/')) return;
    for (const key of [...src.searchParams.keys()]) {
      if (key === 'height' || key === 'crop' || key.startsWith('crop_') || key === 'pad_color') src.searchParams.delete(key);
    }
    // These four themes use a 1600px page cap and the same PDP source-width ladder.
    const needed = (innerWidth >= 768 ? Math.min(innerWidth, 1600) / 2 : innerWidth) * (devicePixelRatio || 1);
    const widths = [...new Set([340, 480, 740, 980, 1200, 1400, 1600, 1920, 2560, 3200, 4000].filter(w => w <= originalWidth).concat(originalWidth))].sort((a, b) => a - b);
    const width = widths.find(w => w >= needed) || widths[widths.length - 1];
    src.searchParams.set('width', String(width));
    if (!images.has(src.href)) {
      const preview = new Image();
      preview.fetchPriority = 'low';
      preview.decoding = 'async';
      images.set(src.href, preview);
      preview.src = src.href;
    }
  };
  document.addEventListener('touchstart', event => warm(event.target.closest('a')), {capture: true, passive: true});
  document.addEventListener('focusin', event => warm(event.target.closest('a')), {passive: true});
  document.addEventListener('mouseover', event => {
    const anchor = event.target.closest('a');
    if (!valid(anchor) || anchor.contains(event.relatedTarget)) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => warm(anchor), 65);
  }, {passive: true});
  document.addEventListener('mouseout', event => {
    const anchor = event.target.closest('a');
    if (anchor && !anchor.contains(event.relatedTarget)) clearTimeout(hoverTimer);
  }, {passive: true});
  const observe = () => {
    // Give the first two product documents time to arrive before a quick card click.
    const cards = [...document.querySelectorAll('#content product-block')];
    for (const card of cards.slice(0, 2)) {
      const anchor = card.querySelector('a.product-link[href]');
      if (anchor) warm(anchor, true);
    }
    if (passiveCount >= 2 || !('IntersectionObserver' in window)) return;
    const timers = new Map();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        clearTimeout(timers.get(entry.target));
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          timers.set(entry.target, setTimeout(() => {
            const anchor = entry.target.querySelector('a.product-link[href]');
            if (anchor) warm(anchor, true);
            observer.unobserve(entry.target);
          }, 250));
        }
      });
      if (passiveCount >= 2) observer.disconnect();
    }, {threshold: [0, 0.5]});
    cards.forEach(card => observer.observe(card));
  };
  const schedule = () => {
    if ('requestIdleCallback' in window) requestIdleCallback(observe, {timeout: 2000});
    else setTimeout(observe, 500);
  };
  if (document.readyState === 'complete') schedule();
  else addEventListener('load', schedule, {once: true});
})();
